import * as moment from 'moment';
import { APP_INITIALIZER, NgModule } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AppComponent } from '@app/app.component';
import { AppConfig } from '@app/app.config';
import { BrowserModule } from '@angular/platform-browser';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { FormsModule } from '@angular/forms';
import { HttpClientModule } from '@angular/common/http';
import { LoginStatusService } from '@app/security/shared/login-status.service';
import { Logger } from '@app/logger/logger';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule, MatIconRegistry } from '@angular/material/icon';
import { MAT_MOMENT_DATE_FORMATS, MomentDateAdapter } from '@angular/material-moment-adapter';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MenuModule } from '@app/menu/menu.module';
import { NoopAnimationsModule } from '@angular/platform-browser/animations';
import { NotificationsModule } from '@app/notifications/shared/notifications.module';
import { SharedModule } from '@app/shared/shared.module';
import { UIModule } from '@app/ui/ui.module';
import { appRoutes } from '@app/app.routes';
import { environment } from '@env/environment';

const lgSfx = '\t(app.module)';

export function ConfigLoader(config: AppConfig): () => Promise<any> {
  return () => config.load();
}

@NgModule({
  declarations: [AppComponent],
  imports: [
    // Angular
    BrowserModule,
    NoopAnimationsModule,
    FormsModule,
    HttpClientModule,

    // Elements needed for index.html
    MatButtonModule,
    MatIconModule,
    MatToolbarModule,
    FontAwesomeModule,

    // AUJS Modules
    appRoutes,

    NotificationsModule,
    SharedModule,

    // Root UI modules
    MenuModule,
    UIModule
  ],
  exports: [],
  providers: [
    {
      provide: APP_INITIALIZER,
      useFactory: ConfigLoader,
      deps: [AppConfig],
      multi: true
    },
    MatIconRegistry,
    { provide: MAT_DATE_LOCALE, useValue: navigator.language },
    { provide: DateAdapter, useClass: MomentDateAdapter, deps: [MAT_DATE_LOCALE] },
    { provide: MAT_DATE_FORMATS, useValue: MAT_MOMENT_DATE_FORMATS }
  ],
  bootstrap: [AppComponent]
})
export class AppModule {
  public pageTitle = 'AUJS';
  private loggedIn = false;

  constructor(
    private appConfig: AppConfig,
    private log: Logger,
    private loginStatusService: LoginStatusService,
    private matIconRegistry: MatIconRegistry,
    private route: ActivatedRoute,
    private router: Router
  ) {
    moment.locale(navigator.language);
    this.setupFontIcons();
    this.watchLoginStatus();
    this.watchRouteChanges();
    this.log.level = environment.logger.level;

    log.info(
      `configuration loaded with legacy URI (${this.appConfig.getLegacyUri()}) and server URI (${this.appConfig.getServerUri()}) ${lgSfx}`
    );
  }

  private watchLoginStatus(): void {
    this.loginStatusService.loginStatus.subscribe((loginStatus) => {
      if (loginStatus.loggedIn === false && this.loggedIn === true) {
        this.log.info(`logout detected, redirecting to login route ${lgSfx}`, loginStatus);
        this.loggedIn = false;
        this.router.navigate(['security', 'login']);
      } else {
        this.loggedIn = loginStatus.loggedIn;
      }
    });
  }

  private setupFontIcons(): void {
    this.matIconRegistry.registerFontClassAlias('fontawesome', 'fa');
    this.matIconRegistry.setDefaultFontSetClass('fa');
  }

  private watchRouteChanges(): void {
    this.route.data.subscribe((data: any) => {
      this.pageTitle = data.title || 'no-title';
    });
  }
}
